import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { ProfileDropdown } from './ProfileDropdown';
import { TabBar } from './TabBar';
import { ClockInFAB } from './ClockInFAB';
import { GroupChat } from '../chat';
import { useMessages } from '@/hooks/useMessages';
import { useAuth } from '@/hooks/useAuth';
import { NotificationCenter } from '../notifications/NotificationCenter';

const LAST_READ_KEY = 'kaiwatch_last_read_message';

export function TopNav() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [scrollToMessageId, setScrollToMessageId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: messages = [] } = useMessages();
  const [lastReadTimestamp, setLastReadTimestamp] = useState<string | null>(() => {
    return localStorage.getItem(LAST_READ_KEY);
  });

  // Calculate unread count - only count messages from OTHER users after last read
  const unreadCount = useMemo(() => {
    if (!user?.id || messages.length === 0) return 0;

    // Filter to only messages from OTHER users
    const otherUsersMessages = messages.filter(m => m.user_id !== user.id);

    if (!lastReadTimestamp || otherUsersMessages.length === 0) {
      return 0; // First time user or no messages from others
    }

    // Count messages from others that were created after last read
    return otherUsersMessages.filter(m => m.created_at > lastReadTimestamp).length;
  }, [messages, lastReadTimestamp, user?.id]);

  // Mark messages as read
  const markAsRead = useCallback(() => {
    const now = new Date().toISOString();
    localStorage.setItem(LAST_READ_KEY, now);
    setLastReadTimestamp(now);
  }, []);

  // Mark as read when chat opens
  const handleOpenChat = () => {
    setIsChatOpen(true);
    markAsRead();
  };

  // Update last read when new messages arrive while chat is open
  useEffect(() => {
    if (isChatOpen && messages.length > 0) {
      markAsRead();
    }
  }, [isChatOpen, messages, markAsRead]);

  return (
    <>
      <header data-testid="topnav" className="sticky top-0 z-40 bg-background border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-xl font-bold text-white hover:text-accent transition-colors"
              data-testid="clickable-topnav-logo"
            >
              <svg className="w-8 h-8 text-accent" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              <span>KaiWatch</span>
            </Link>

            {/* Desktop tabs - hidden on mobile, shown in TabBar instead */}
            <nav className="hidden lg:flex items-center space-x-1">
              {/* Tabs are now in TabBar component below header */}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Notification center */}
              <NotificationCenter
                onMentionClick={(messageId) => {
                  setScrollToMessageId(messageId);
                  setIsChatOpen(true);
                  markAsRead();
                }}
                onShiftClick={(date) => {
                  navigate(`/dashboard/calendar`);
                }}
              />

              {/* Chat button with notification badge */}
              <button
                onClick={handleOpenChat}
                className="relative p-2 hover:bg-slate-700 rounded-lg transition-colors"
                title="Family Chat"
                data-testid="clickable-chat-open"
              >
                <MessageCircle size={22} className="text-gray-400 hover:text-white" />
                {unreadCount > 0 && (
                  <span data-testid="container-chat-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Profile dropdown */}
              <ProfileDropdown />
            </div>
          </div>
        </div>
      </header>

      {/* Group Chat Modal */}
      <GroupChat
        isOpen={isChatOpen}
        onClose={() => {
          setIsChatOpen(false);
          setScrollToMessageId(null);
        }}
        scrollToMessageId={scrollToMessageId}
      />
    </>
  );
}

// Combined layout with TopNav and TabBar
export function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <TabBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>
      <ClockInFAB />
    </div>
  );
}

export default TopNav;
